import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    User, Store, Mail, Phone, MapPin, CreditCard,
    Save, Camera, Shield, Bell, Eye, EyeOff, FileCheck, CheckCircle,
    Clock, XCircle, Loader2, FileText, ExternalLink, Settings, Sparkles, RotateCcw,
    FileAudio, Play
} from 'lucide-react';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import axios from 'axios';
import VendorOnboarding from '../components/VendorOnboarding';

const VendorProfile = () => {
    const { user, token } = useAuth();
    const [activeTab, setActiveTab] = useState('business');
    const [showPassword, setShowPassword] = useState(false);
    const [vendorData, setVendorData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showOnboarding, setShowOnboarding] = useState(false);

    const [businessInfo, setBusinessInfo] = useState({
        storeName: '',
        description: '',
        email: '',
        phone: '',
        address: '',
        website: ''
    });

    const [saving, setSaving] = useState(false);

    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [notifications, setNotifications] = useState({
        orderAlerts: true,
        emailNotifications: true,
        smsNotifications: false,
        weeklyReport: true
    });

    useEffect(() => {
        if (user?.id) {
            fetchVendorProfile();
        }
    }, [user?.id]);

    const fetchVendorProfile = async () => {
        if (!user?.id) return;

        try {
            const response = await axios.get(`${API}/api/vendors/${user.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setVendorData(response.data);
            setBusinessInfo({
                storeName: response.data.business_name || '',
                description: response.data.description || '',
                email: response.data.email || '',
                phone: response.data.phone || '',
                address: response.data.business_address ?
                    `${response.data.business_address}, ${response.data.city}, ${response.data.state} - ${response.data.pincode}` : '',
                website: ''
            });
        } catch (error) {
            // Fallback to user data if vendor endpoint fails
            setBusinessInfo({
                storeName: user?.name || 'Your Store',
                description: '',
                email: user?.email || '',
                phone: '',
                address: '',
                website: ''
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSaveBusinessInfo = (e) => {
        e.preventDefault();
        toast.success('Business information updated successfully!');
    };

    const handleChangePassword = (e) => {
        e.preventDefault();
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        if (passwordForm.newPassword.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }
        toast.success('Password changed successfully!');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    };

    const handleSaveNotifications = () => {
        toast.success('Notification preferences saved!');
    };

    const handleAudioUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('audio/')) {
            return toast.error("Please select a valid audio file (mp3, wav)");
        }

        const formData = new FormData();
        formData.append('file', file);
        setSaving(true);
        try {
            const uploadRes = await axios.post(`${API}/api/upload/public`, formData, { 
                headers: { Authorization: `Bearer ${token}` }
            });
            const audioUrl = uploadRes.data.url;
            
            await axios.put(`${API}/api/auth/profile`, { 
                notification_sound_url: audioUrl 
            }, { 
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success("Notification sound updated! (Refresh to apply everywhere)");
        } catch (err) {
            toast.error("Failed to upload audio file");
        }
        setSaving(false);
    };

    const playTestSound = () => {
        const soundUrl = user?.notification_sound_url;
        if (soundUrl) {
            new Audio(API + soundUrl).play().catch(e => toast.error("Browser blocked audio playback"));
        } else {
            toast.info("No custom sound currently set. The default synthetic chime will play.");
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'approved':
                return (
                    <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                        <CheckCircle className="w-3 h-3" />
                        Verified Vendor
                    </span>
                );
            case 'pending':
                return (
                    <span className="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full font-medium">
                        <Clock className="w-3 h-3" />
                        Pending Approval
                    </span>
                );
            case 'rejected':
                return (
                    <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                        <XCircle className="w-3 h-3" />
                        Application Rejected
                    </span>
                );
            default:
                return null;
        }
    };

    const tabs = [
        { id: 'business', label: 'Business Info', icon: Store },
        { id: 'documents', label: 'Documents', icon: FileCheck },
        { id: 'payment', label: 'Payment', icon: CreditCard },
        { id: 'security', label: 'Security', icon: Shield },
        { id: 'notifications', label: 'Notifications', icon: Bell },
        { id: 'settings', label: 'Settings', icon: Settings }
    ];

    if (loading) {
        return (
            <DashboardLayout userRole="vendor">
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout userRole="vendor">
            <div className="space-y-6">
                {/* Header */}
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold">Vendor Profile</h1>
                    <p className="text-muted-foreground mt-1">Manage your store information and settings</p>
                </div>

                {/* Status Alert for Pending/Rejected */}
                {vendorData?.status === 'pending' && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                        <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium text-yellow-800">Application Under Review</p>
                            <p className="text-sm text-yellow-700 mt-1">
                                Your vendor application is currently being reviewed. You will be notified via email once approved.
                            </p>
                        </div>
                    </div>
                )}

                {vendorData?.status === 'rejected' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                        <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium text-red-800">Application Rejected</p>
                            <p className="text-sm text-red-700 mt-1">
                                Unfortunately, your vendor application was not approved. Please contact support for more information.
                            </p>
                        </div>
                    </div>
                )}

                {/* Profile Header Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-card rounded-xl border p-6 flex flex-col md:flex-row items-center gap-6"
                >
                    <div className="relative">
                        <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center">
                            <Store className="w-12 h-12 text-primary" />
                        </div>
                        <button className="absolute bottom-0 right-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white hover:bg-primary/90 transition-colors">
                            <Camera className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="text-center md:text-left">
                        <h2 className="text-xl font-bold">{vendorData?.business_name || businessInfo.storeName}</h2>
                        <p className="text-muted-foreground">{user?.email}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                            {getStatusBadge(vendorData?.status)}
                            {vendorData?.business_type && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium capitalize">
                                    {vendorData.business_type}
                                </span>
                            )}
                        </div>
                    </div>
                </motion.div>

                {/* Tabs */}
                <div className="flex gap-2 border-b overflow-x-auto">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors whitespace-nowrap ${activeTab === tab.id
                                ? 'text-primary border-b-2 border-primary'
                                : 'text-muted-foreground hover:text-foreground'
                                }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    {/* Business Info Tab */}
                    {activeTab === 'business' && (
                        <div className="space-y-6">
                            {/* Business Details Card */}
                            {vendorData && (
                                <div className="bg-card rounded-xl border p-6 space-y-4">
                                    <h3 className="font-semibold text-lg flex items-center gap-2">
                                        <Store className="w-5 h-5 text-primary" />
                                        Business Details
                                    </h3>
                                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                                        <div className="bg-muted/30 rounded-lg p-4">
                                            <p className="text-muted-foreground mb-1">GST Number</p>
                                            <p className="font-mono font-medium">{vendorData.gst_number || 'Not provided'}</p>
                                        </div>
                                        <div className="bg-muted/30 rounded-lg p-4">
                                            <p className="text-muted-foreground mb-1">PAN Number</p>
                                            <p className="font-mono font-medium">{vendorData.pan_number || 'Not provided'}</p>
                                        </div>
                                        <div className="bg-muted/30 rounded-lg p-4 md:col-span-2">
                                            <p className="text-muted-foreground mb-1">Business Address</p>
                                            <p className="font-medium">
                                                {vendorData.business_address ?
                                                    `${vendorData.business_address}, ${vendorData.city}, ${vendorData.state} - ${vendorData.pincode}`
                                                    : 'Not provided'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Editable Form */}
                            <form onSubmit={handleSaveBusinessInfo} className="bg-card rounded-xl border p-6 space-y-6">
                                <h3 className="font-semibold text-lg">Edit Information</h3>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <Label htmlFor="storeName">Store Name</Label>
                                        <Input
                                            id="storeName"
                                            value={businessInfo.storeName}
                                            onChange={(e) => setBusinessInfo({ ...businessInfo, storeName: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            value={businessInfo.email}
                                            onChange={(e) => setBusinessInfo({ ...businessInfo, email: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <Label htmlFor="phone">Phone</Label>
                                        <Input
                                            id="phone"
                                            value={businessInfo.phone}
                                            onChange={(e) => setBusinessInfo({ ...businessInfo, phone: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="website">Website</Label>
                                        <Input
                                            id="website"
                                            value={businessInfo.website}
                                            onChange={(e) => setBusinessInfo({ ...businessInfo, website: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="description">Store Description</Label>
                                    <Textarea
                                        id="description"
                                        value={businessInfo.description}
                                        onChange={(e) => setBusinessInfo({ ...businessInfo, description: e.target.value })}
                                        rows={4}
                                    />
                                </div>
                                <Button type="submit" className="btn-primary">
                                    <Save className="w-4 h-4 mr-2" /> Save Changes
                                </Button>
                            </form>
                        </div>
                    )}

                    {/* Documents Tab */}
                    {activeTab === 'documents' && (
                        <div className="bg-card rounded-xl border p-6 space-y-6">
                            <h3 className="font-semibold text-lg flex items-center gap-2">
                                <FileCheck className="w-5 h-5 text-primary" />
                                Uploaded Documents
                            </h3>

                            {vendorData?.documents ? (
                                <div className="grid md:grid-cols-2 gap-4">
                                    {[
                                        { key: 'pan_card_url', label: 'PAN Card', mandatory: true },
                                        { key: 'gst_certificate_url', label: 'GST Certificate', mandatory: true },
                                        { key: 'bis_certification_url', label: 'BIS Certification', mandatory: true },
                                        { key: 'mnre_registration_url', label: 'MNRE Registration', mandatory: true },
                                        { key: 'nabl_test_reports_url', label: 'NABL Test Reports', mandatory: false },
                                        { key: 'manufacturer_authorization_url', label: 'Manufacturer Authorization', mandatory: false },
                                        { key: 'company_registration_url', label: 'Company Registration', mandatory: false },
                                        { key: 'bill_of_entry_url', label: 'Bill of Entry', mandatory: false },
                                        { key: 'iec_code_url', label: 'IEC Code', mandatory: false }
                                    ].map(doc => (
                                        <div
                                            key={doc.key}
                                            className={`flex items-center justify-between p-4 rounded-lg border ${vendorData.documents[doc.key]
                                                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                                : 'bg-muted/30'
                                                }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                {vendorData.documents[doc.key] ? (
                                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                                ) : (
                                                    <FileText className="w-5 h-5 text-muted-foreground" />
                                                )}
                                                <div>
                                                    <p className="font-medium text-sm">{doc.label}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {doc.mandatory ? 'Mandatory' : 'Optional'}
                                                    </p>
                                                </div>
                                            </div>
                                            {vendorData.documents[doc.key] && (
                                                <a
                                                    href={`${API}${vendorData.documents[doc.key]}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                                                >
                                                    <ExternalLink className="w-3 h-3" />
                                                    View
                                                </a>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>No documents uploaded yet</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Payment Tab */}
                    {activeTab === 'payment' && (
                        <div className="bg-card rounded-xl border p-6 space-y-6">
                            <h3 className="font-semibold text-lg flex items-center gap-2">
                                <CreditCard className="w-5 h-5 text-primary" />
                                Bank Details
                            </h3>

                            {vendorData?.bank_details ? (
                                <div className="grid md:grid-cols-2 gap-4 text-sm">
                                    <div className="bg-muted/30 rounded-lg p-4">
                                        <p className="text-muted-foreground mb-1">Bank Name</p>
                                        <p className="font-medium">{vendorData.bank_details.bank_name}</p>
                                    </div>
                                    <div className="bg-muted/30 rounded-lg p-4">
                                        <p className="text-muted-foreground mb-1">Account Holder</p>
                                        <p className="font-medium">{vendorData.bank_details.account_holder_name}</p>
                                    </div>
                                    <div className="bg-muted/30 rounded-lg p-4">
                                        <p className="text-muted-foreground mb-1">Account Number</p>
                                        <p className="font-mono font-medium">****{vendorData.bank_details.account_number?.slice(-4)}</p>
                                    </div>
                                    <div className="bg-muted/30 rounded-lg p-4">
                                        <p className="text-muted-foreground mb-1">IFSC Code</p>
                                        <p className="font-mono font-medium">{vendorData.bank_details.ifsc_code}</p>
                                    </div>
                                    {vendorData.bank_details.branch_name && (
                                        <div className="bg-muted/30 rounded-lg p-4 md:col-span-2">
                                            <p className="text-muted-foreground mb-1">Branch</p>
                                            <p className="font-medium">{vendorData.bank_details.branch_name}</p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>Bank details not provided</p>
                                </div>
                            )}

                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm">
                                <p className="font-medium text-yellow-800">Secure Payment Information</p>
                                <p className="text-yellow-700 mt-1">Your bank details are encrypted and securely stored. Contact support to update bank details.</p>
                            </div>
                        </div>
                    )}

                    {/* Security Tab */}
                    {activeTab === 'security' && (
                        <form onSubmit={handleChangePassword} className="bg-card rounded-xl border p-6 space-y-6">
                            <h3 className="font-semibold text-lg">Change Password</h3>
                            <div className="space-y-4 max-w-md">
                                <div className="space-y-2">
                                    <Label htmlFor="currentPassword">Current Password</Label>
                                    <div className="relative">
                                        <Input
                                            id="currentPassword"
                                            type={showPassword ? 'text' : 'password'}
                                            value={passwordForm.currentPassword}
                                            onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="newPassword">New Password</Label>
                                    <Input
                                        id="newPassword"
                                        type="password"
                                        value={passwordForm.newPassword}
                                        onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                                    <Input
                                        id="confirmPassword"
                                        type="password"
                                        value={passwordForm.confirmPassword}
                                        onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                                    />
                                </div>
                            </div>
                            <Button type="submit" className="btn-primary">
                                <Shield className="w-4 h-4 mr-2" /> Change Password
                            </Button>
                        </form>
                    )}

                    {/* Notifications Tab */}
                    {activeTab === 'notifications' && (
                        <div className="bg-card rounded-xl border p-6 space-y-6">
                            <h3 className="font-semibold text-lg flex items-center gap-2"><Bell className="w-5 h-5 text-primary" /> Notification Preferences</h3>
                            
                            <div className="bg-muted/30 p-5 rounded-lg border border-border/50">
                                <h4 className="font-medium text-sm text-foreground/80 mb-4 flex items-center gap-2">
                                    <FileAudio className="w-4 h-4 text-indigo-500" /> Custom Notification Sound
                                </h4>
                                <div className="flex flex-col md:flex-row items-center gap-4">
                                    <Input 
                                        type="file" 
                                        accept="audio/mp3,audio/wav,audio/ogg" 
                                        onChange={handleAudioUpload}
                                        disabled={saving}
                                        className="bg-background cursor-pointer max-w-sm"
                                    />
                                    <Button onClick={playTestSound} variant="outline" className="gap-2">
                                        <Play className="w-4 h-4" /> Play Test
                                    </Button>
                                </div>
                                <p className="text-xs text-muted-foreground mt-3">
                                    Upload an audio file (MP3, WAV) to override the default system chime. This sound is specific to your account across all login devices.
                                </p>
                            </div>

                            <div className="space-y-4">
                                {[
                                    { key: 'orderAlerts', label: 'Order Alerts', desc: 'Get notified when you receive new orders' },
                                    { key: 'emailNotifications', label: 'Email Notifications', desc: 'Receive updates via email' },
                                    { key: 'smsNotifications', label: 'SMS Notifications', desc: 'Receive updates via SMS' },
                                    { key: 'weeklyReport', label: 'Weekly Reports', desc: 'Receive weekly sales and performance reports' }
                                ].map((item) => (
                                    <div key={item.key} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                                        <div>
                                            <p className="font-medium">{item.label}</p>
                                            <p className="text-sm text-muted-foreground">{item.desc}</p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={notifications[item.key]}
                                                onChange={(e) => setNotifications({ ...notifications, [item.key]: e.target.checked })}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                        </label>
                                    </div>
                                ))}
                            </div>
                            <Button onClick={handleSaveNotifications} className="btn-primary">
                                <Save className="w-4 h-4 mr-2" /> Save Preferences
                            </Button>
                        </div>
                    )}

                    {/* Settings Tab */}
                    {activeTab === 'settings' && (
                        <div className="space-y-6">
                            {/* Onboarding Section */}
                            <div className="bg-card rounded-xl border p-6 space-y-4">
                                <h3 className="font-semibold text-lg flex items-center gap-2">
                                    <Sparkles className="w-5 h-5 text-primary" />
                                    Onboarding & Help
                                </h3>
                                <div className="bg-muted/30 rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-medium">Vendor Onboarding Wizard</p>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                Take the guided tour again to learn about your dashboard features
                                            </p>
                                        </div>
                                        <Button
                                            onClick={() => {
                                                localStorage.removeItem('vendor_onboarding_completed');
                                                localStorage.removeItem('vendor_onboarding_step');
                                                setShowOnboarding(true);
                                            }}
                                            variant="outline"
                                            className="gap-2"
                                        >
                                            <RotateCcw className="w-4 h-4" />
                                            Restart Wizard
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Account Section */}
                            <div className="bg-card rounded-xl border p-6 space-y-4">
                                <h3 className="font-semibold text-lg flex items-center gap-2">
                                    <Settings className="w-5 h-5 text-primary" />
                                    Account Settings
                                </h3>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                                        <div>
                                            <p className="font-medium">Account Status</p>
                                            <p className="text-sm text-muted-foreground">Your current vendor status</p>
                                        </div>
                                        {getStatusBadge(vendorData?.status)}
                                    </div>
                                    <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                                        <div>
                                            <p className="font-medium">Member Since</p>
                                            <p className="text-sm text-muted-foreground">When you joined SolarSavers</p>
                                        </div>
                                        <span className="text-sm font-medium">
                                            {vendorData?.created_at ? new Date(vendorData.created_at).toLocaleDateString() : 'N/A'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Danger Zone */}
                            <div className="bg-card rounded-xl border border-red-200 p-6 space-y-4">
                                <h3 className="font-semibold text-lg text-red-600">Danger Zone</h3>
                                <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                                    <div>
                                        <p className="font-medium text-red-800">Deactivate Account</p>
                                        <p className="text-sm text-red-600">Temporarily disable your vendor account</p>
                                    </div>
                                    <Button
                                        variant="outline"
                                        className="border-red-300 text-red-600 hover:bg-red-50"
                                        onClick={() => {
                                            if (window.confirm('Are you sure you want to deactivate your account? This action can be reversed by contacting support.')) {
                                                toast.info('Account deactivation request sent. Our team will contact you shortly.');
                                            }
                                        }}
                                    >
                                        Deactivate
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </motion.div>

                {/* Onboarding Overlay */}
                {showOnboarding && (
                    <VendorOnboarding
                        onComplete={() => setShowOnboarding(false)}
                        vendorName={vendorData?.business_name || user?.name}
                    />
                )}
            </div>
        </DashboardLayout>
    );
};

export default VendorProfile;
